/**
 * HR Bench — Monthly HR Input Control (Stage 73.20v Phase 1).
 *
 * The controlled funnel for ALL employee-lifecycle + monthly-payroll inputs.
 * HR creates an entry → walks it through draft → submitted → hr_approved →
 * payroll_review → payroll_accepted. On `payroll_accepted` the backend
 * mutates the employee master (lifecycle) or emits a `monthly_inputs` row
 * (variable pay) so the payroll engine picks it up automatically.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Loader2, Plus, Search, Filter, Send, CheckCircle2, XCircle,
  UserCheck, ShieldCheck, FileSpreadsheet, Upload, Download,
  ListChecks, X, FileText, History, ArrowRight, Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATE_TONE = {
  draft:             { bg: 'bg-slate-100',   txt: 'text-slate-700',   bd: 'border-slate-300',   label: 'Draft' },
  submitted:         { bg: 'bg-sky-50',      txt: 'text-sky-700',     bd: 'border-sky-300',     label: 'Submitted' },
  hr_approved:       { bg: 'bg-violet-50',   txt: 'text-violet-700',  bd: 'border-violet-300',  label: 'HR Approved' },
  payroll_review:    { bg: 'bg-amber-50',    txt: 'text-amber-800',   bd: 'border-amber-300',   label: 'Payroll Review' },
  payroll_accepted:  { bg: 'bg-emerald-50',  txt: 'text-emerald-700', bd: 'border-emerald-300', label: 'Payroll Accepted' },
  processed:         { bg: 'bg-blue-50',     txt: 'text-blue-700',    bd: 'border-blue-300',    label: 'Processed' },
  locked:            { bg: 'bg-slate-200',   txt: 'text-slate-800',   bd: 'border-slate-400',   label: 'Locked' },
  rejected:          { bg: 'bg-rose-50',     txt: 'text-rose-700',    bd: 'border-rose-300',    label: 'Rejected' },
};

const STATE_NEXT_LABEL = {
  draft:           { to: 'submitted',         label: 'Submit',         icon: Send },
  submitted:       { to: 'hr_approved',       label: 'HR Approve',     icon: UserCheck },
  hr_approved:     { to: 'payroll_review',    label: 'Send to Payroll',icon: ArrowRight },
  payroll_review:  { to: 'payroll_accepted',  label: 'Accept for Run', icon: ShieldCheck },
};

const TRANSITION_PATH = {
  submitted: '/submit',
  hr_approved: '/hr-approve',
  payroll_review: '/payroll-review',
  payroll_accepted: '/payroll-accept',
};

export default function HRBench() {
  const [activeCompany] = useActiveCompany();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ category: 'all', status: 'all', q: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/hr-bench/_meta`, { withCredentials: true })
      .then(r => setMeta(r.data))
      .catch(() => toast.error('Failed to load metadata'));
  }, []);


  const loadAll = useCallback(async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const q = { company_id: activeCompany };
      if (filters.category !== 'all') q.category = filters.category;
      if (filters.status !== 'all') q.status = filters.status;
      const qs = new URLSearchParams(q).toString();
      const [eRes, sRes] = await Promise.all([
        axios.get(`${API}/hr-bench/entries?${qs}`, { withCredentials: true }),
        axios.get(`${API}/hr-bench/stats?company_id=${activeCompany}`, { withCredentials: true }),
      ]);
      setEntries(eRes.data || []);
      setStats(sRes.data || null);
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [activeCompany, filters.category, filters.status]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredEntries = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e =>
      (e.remarks || '').toLowerCase().includes(q) ||
      (e.payload?.remarks || '').toLowerCase().includes(q) ||
      (e._employee?.name || '').toLowerCase().includes(q) ||
      (e._employee?.code || '').toLowerCase().includes(q),
    );
  }, [entries, filters.q]);

  // Bulk actions
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEntries.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredEntries.map(e => e.id)));
  };
  const runBulk = async (action) => {
    if (selectedIds.size === 0) { toast.error('No entries selected'); return; }
    let remarks = '';
    if (action === 'reject') {
      remarks = prompt('Rejection remarks (required for all selected entries):') || '';
      if (!remarks.trim()) return;
    }
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/hr-bench/bulk-action`,
        { entry_ids: Array.from(selectedIds), action, remarks },
        { withCredentials: true });
      const { succeeded = 0, failed = 0 } = r.data;
      if (failed > 0) toast.warning(`${succeeded} succeeded, ${failed} failed`);
      else toast.success(`${succeeded} entries actioned`);
      setSelectedIds(new Set());
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBulkBusy(false);
  };

  const categoryByKey = useMemo(() => {
    if (!meta) return {};
    return Object.fromEntries(meta.categories.map(c => [c.key, c]));
  }, [meta]);

  if (!meta) {
    return <MainLayout><div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="hr-bench-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <ListChecks className="w-7 h-7 text-violet-600" />
              HR Bench — Monthly HR Input Control
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              Controlled funnel for every employee-lifecycle and monthly-payroll input.
              Approved entries flow automatically into payroll — no manual re-entry.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowBulk(true)} data-testid="hr-bench-bulk-btn">
              <Upload className="w-4 h-4 mr-1" /> Bulk CSV
            </Button>
            <Button
              data-testid="hr-bench-create-btn"
              onClick={() => setShowCreate(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" /> New Entry
            </Button>
          </div>
        </div>

        {/* Reuses existing modules — clarity card */}
        <Card className="p-4 bg-violet-50 border-violet-200" data-testid="hr-bench-reuse-card">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-violet-700 shrink-0 mt-0.5" />
            <div className="text-xs text-violet-900">
              <strong>HR Bench is the controlled input layer — not a second payroll engine.</strong>{' '}
              All approved entries flow into the existing modules:
              <ul className="mt-1 list-disc list-inside space-y-0.5 text-violet-800">
                <li>Lifecycle entries mutate <code className="bg-violet-100 px-1 rounded text-[10px]">employees</code> + push to <code className="bg-violet-100 px-1 rounded text-[10px]">employees.history</code> (same schema as direct admin actions like Promotion / Separation).</li>
                <li>Monthly variable pay (incentive / bonus / arrear / recovery / deductions / OT / reimbursement) upserts the canonical <code className="bg-violet-100 px-1 rounded text-[10px]">monthly_inputs</code> row (fixed columns, additive). The payroll engine already merges these — no new engine code.</li>
                <li>Arrears also create a row in <code className="bg-violet-100 px-1 rounded text-[10px]">db.arrears</code> for the existing arrear processor.</li>
                <li>Salary revisions push old CTC to <code className="bg-violet-100 px-1 rounded text-[10px]">salary_history</code> so historical runs remain reproducible.</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Status strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {meta.workflow_states.map(s => {
              const tone = STATE_TONE[s] || STATE_TONE.draft;
              const count = stats.by_status?.[s] || 0;
              return (
                <button
                  key={s}
                  data-testid={`hr-bench-stat-${s}`}
                  onClick={() => setFilters(f => ({ ...f, status: s }))}
                  className={`rounded-lg border p-3 text-left transition ${tone.bg} ${tone.bd} ${tone.txt} ${filters.status === s ? 'ring-2 ring-violet-400' : 'hover:ring-1 hover:ring-slate-300'}`}>
                  <div className="text-xl font-bold">{count}</div>
                  <div className="text-[10px] uppercase tracking-wide mt-1 truncate">{tone.label}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Filter bar */}
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              data-testid="hr-bench-category-filter"
              value={filters.category}
              onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
              className="px-3 py-1.5 border rounded">
              <option value="all">All categories</option>
              {meta.categories.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <select
              data-testid="hr-bench-status-filter"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="px-3 py-1.5 border rounded">
              <option value="all">All states</option>
              {meta.workflow_states.map(s => <option key={s} value={s}>{STATE_TONE[s]?.label || s}</option>)}
            </select>
            <div className="relative ml-auto">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="hr-bench-search"
                value={filters.q}
                onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                placeholder="Search by employee, code, remarks..."
                className="pl-7 pr-3 py-1.5 border rounded text-xs w-64" />
            </div>
            <Button size="sm" variant="ghost" onClick={loadAll} disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </Card>

        {/* Bulk action toolbar (visible when selections exist) */}
        {selectedIds.size > 0 && (
          <Card className="p-3 bg-violet-50 border-violet-300 sticky top-2 z-10" data-testid="hr-bench-bulk-toolbar">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs font-semibold text-violet-900">
                {selectedIds.size} entr{selectedIds.size === 1 ? 'y' : 'ies'} selected
              </div>
              <div className="flex gap-1 flex-wrap">
                <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('submit')} className="text-xs bg-sky-600 hover:bg-sky-700 text-white" data-testid="bulk-submit-btn">
                  <Send className="w-3 h-3 mr-1" />Submit
                </Button>
                <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('hr_approve')} className="text-xs bg-violet-600 hover:bg-violet-700 text-white" data-testid="bulk-hr-approve-btn">
                  <UserCheck className="w-3 h-3 mr-1" />HR Approve
                </Button>
                <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('payroll_review')} className="text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="bulk-payroll-review-btn">
                  <ArrowRight className="w-3 h-3 mr-1" />Send to Payroll
                </Button>
                <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('payroll_accept')} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-payroll-accept-btn">
                  <ShieldCheck className="w-3 h-3 mr-1" />Payroll Accept
                </Button>
                <Button size="sm" disabled={bulkBusy} variant="outline" onClick={() => runBulk('reject')} className="text-xs text-rose-700" data-testid="bulk-reject-btn">
                  <XCircle className="w-3 h-3 mr-1" />Reject
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="text-xs">Clear</Button>
              </div>
            </div>
          </Card>
        )}

        {/* Entries table */}
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox"
                      data-testid="hr-bench-select-all"
                      checked={filteredEntries.length > 0 && selectedIds.size === filteredEntries.length}
                      onChange={toggleSelectAll} />
                  </th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Employee</th>
                  <th className="text-left px-4 py-2">Payload</th>
                  <th className="text-left px-4 py-2">Effective</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                    {loading ? 'Loading…' : 'No entries match the filters. Click "New Entry" to start.'}
                  </td></tr>
                )}
                {filteredEntries.map(e => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    category={categoryByKey[e.category]}
                    selected={selectedIds.has(e.id)}
                    onToggleSelect={() => toggleSelect(e.id)}
                    onOpen={() => setDetailEntry(e)}
                    onDelete={() => setDeleteTarget(e)}
                    onChanged={loadAll}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {showCreate && (
        <CreateEntryModal
          meta={meta}
          companyId={activeCompany}
          onClose={() => setShowCreate(false)}
          onCreated={(e) => { setShowCreate(false); setDetailEntry(e); loadAll(); }}
        />
      )}

      {showBulk && (
        <BulkUploadModal
          meta={meta}
          companyId={activeCompany}
          onClose={() => setShowBulk(false)}
          onUploaded={() => { setShowBulk(false); loadAll(); }}
        />
      )}

      {detailEntry && (
        <EntryDetailDrawer
          entryId={detailEntry.id}
          category={categoryByKey[detailEntry.category]}
          onClose={() => setDetailEntry(null)}
          onDelete={() => setDeleteTarget(detailEntry)}
          onChanged={() => { loadAll(); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          entry={deleteTarget}
          category={categoryByKey[deleteTarget.category]}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); setDetailEntry(null); loadAll(); }}
        />
      )}
    </MainLayout>
  );
}

function EntryRow({ entry, category, selected, onToggleSelect, onOpen, onDelete, onChanged }) {
  const [busy, setBusy] = useState(false);
  const tone = STATE_TONE[entry.status] || STATE_TONE.draft;
  const next = STATE_NEXT_LABEL[entry.status];

  const advance = async () => {
    if (!next) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/hr-bench/entries/${entry.id}${TRANSITION_PATH[next.to]}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Moved to ${STATE_TONE[next.to].label}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const NextIcon = next?.icon;
  const payloadPreview = (() => {
    const p = entry.payload || {};
    const parts = [];
    if (p.amount) parts.push(`₹${Number(p.amount).toLocaleString('en-IN')}`);
    if (p.new_ctc) parts.push(`CTC → ₹${Number(p.new_ctc).toLocaleString('en-IN')}`);
    if (p.new_designation) parts.push(`→ ${p.new_designation}`);
    if (p.new_department) parts.push(`→ ${p.new_department}`);
    if (p.new_location) parts.push(`→ ${p.new_location}`);
    if (p.last_working_day) parts.push(`LWD ${p.last_working_day}`);
    if (p.hold_reason) parts.push(p.hold_reason.replace(/_/g, ' '));
    if (p.statutory_year && p.statutory_month) parts.push(`${p.statutory_month}/${p.statutory_year}`);
    return parts.join(' • ') || (entry.remarks || '—');
  })();

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`hr-bench-row-${entry.id}`}>
      <td className="px-3 py-3">
        <input type="checkbox"
          data-testid={`hr-bench-select-${entry.id}`}
          checked={!!selected}
          onChange={onToggleSelect} />
      </td>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-800">{category?.label || entry.category}</div>
        <div className="text-[10px] text-slate-500 uppercase">{category?.group}</div>
      </td>
      <td className="px-4 py-3 text-slate-700">
        <div className="font-medium text-slate-800">
          {entry._employee?.name || entry.employee_name || (entry.employee_id && !/^[0-9a-fA-F]{24}$/.test(entry.employee_id) ? entry.employee_id : <span className="text-slate-400">Unknown Employee</span>)}
        </div>
        {(entry._employee?.code || entry.employee_code) && (
          <div className="text-xs text-slate-500 font-mono">{entry._employee?.code || entry.employee_code}</div>
        )}
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs max-w-xs truncate" title={payloadPreview}>{payloadPreview}</td>
      <td className="px-4 py-3 text-xs text-slate-600">{entry.effective_date || '—'}</td>
      <td className="px-4 py-3">
        <span className={`text-[11px] font-medium px-2 py-1 rounded border ${tone.bg} ${tone.txt} ${tone.bd}`}>
          {tone.label}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
        <div className="text-[10px]">{entry.created_by_name}</div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={onOpen} data-testid={`hr-bench-open-${entry.id}`} title="View Details">
            <FileText className="w-3.5 h-3.5" />
          </Button>
          {next && (
            <Button
              size="sm"
              onClick={advance}
              disabled={busy}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid={`hr-bench-advance-${entry.id}`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <NextIcon className="w-3.5 h-3.5 mr-1" />}
              {next.label}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 px-2"
            title="Delete Entry"
            data-testid={`hr-bench-delete-${entry.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function CreateEntryModal({ meta, companyId, onClose, onCreated }) {
  const [category, setCategory] = useState(meta.categories[0].key);
  const [employeeId, setEmployeeId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [payload, setPayload] = useState({});
  const [saving, setSaving] = useState(false);

  const catDef = meta.categories.find(c => c.key === category);

  const submit = async () => {
    if (!companyId) { toast.error('Select a company first'); return; }
    // Stage 73.20z — for new_joiner create_new, the employee will be created
    // by the backend at apply-time; only require employeeId for everything
    // else and for activate_existing.
    const njMode = (payload.creation_mode || 'create_new');
    const requiresEmployee = category !== 'new_joiner' || njMode === 'activate_existing';
    if (requiresEmployee && !employeeId) {
      toast.error('Pick an employee for this category'); return;
    }
    if (category === 'new_joiner' && njMode === 'create_new') {
      const required = { first_name: 'First Name', date_of_birth: 'Date of Birth', date_of_joining: 'Date of Joining', mobile: 'Mobile' };
      const missing = Object.entries(required).filter(([k]) => !(payload[k] || '').trim()).map(([_, v]) => v);
      if (missing.length) { toast.error('Missing required fields: ' + missing.join(', ')); return; }
    }
    if (category === 'salary_hold' && !payload.hold_reason) {
      toast.error('Hold reason is required'); return;
    }
    setSaving(true);
    try {
      const body = { category, employee_id: employeeId || null, company_id: companyId,
                     effective_date: effectiveDate || null, remarks, payload };
      const r = await axios.post(`${API}/hr-bench/entries`, body, { withCredentials: true });
      toast.success('Entry created in draft state');
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const setP = (k, v) => setPayload(p => ({ ...p, [k]: v }));

  // Stage 73.20z — new_joiner mode toggle. When create_new, employee picker
  // is hidden because the employee will be CREATED at apply time.
  const isNewJoiner = category === 'new_joiner';
  const njMode = (payload.creation_mode || 'create_new');
  const showEmployeePicker = !isNewJoiner || njMode === 'activate_existing';
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="hr-bench-create-modal">
      <Card className="max-w-2xl w-full p-6 bg-white my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">New HR Bench Entry</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3 text-sm">
          {/* Category — grouped by .group */}
          <div>
            <label className="block text-xs font-medium mb-1">Category *</label>
            <select
              data-testid="hr-bench-category-select"
              value={category}
              onChange={e => { setCategory(e.target.value); setPayload({}); }}
              className="w-full px-3 py-2 border rounded text-sm">
              {Array.from(new Set(meta.categories.map(c => c.group))).map(g => (
                <optgroup key={g} label={g}>
                  {meta.categories.filter(c => c.group === g).map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Stage 73.20z — new_joiner creation_mode toggle */}
          {isNewJoiner && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3" data-testid="hr-bench-new-joiner-mode">
              <p className="text-xs font-semibold text-slate-700 mb-2">How are you adding this joiner?</p>
              <div className="flex gap-2">
                <label className="flex-1 flex items-start gap-2 p-2 rounded border bg-white cursor-pointer hover:bg-blue-50">
                  <input
                    type="radio"
                    name="creation_mode"
                    value="create_new"
                    checked={njMode === 'create_new'}
                    onChange={e => setP('creation_mode', e.target.value)}
                    data-testid="hr-bench-mode-create-new"
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-xs font-semibold text-slate-900">Create New Employee</p>
                    <p className="text-[10px] text-slate-500">Brand new joiner — capture all master fields + statutory + bank. Employee code auto-generated on approval.</p>
                  </div>
                </label>
                <label className="flex-1 flex items-start gap-2 p-2 rounded border bg-white cursor-pointer hover:bg-blue-50">
                  <input
                    type="radio"
                    name="creation_mode"
                    value="activate_existing"
                    checked={njMode === 'activate_existing'}
                    onChange={e => setP('creation_mode', e.target.value)}
                    data-testid="hr-bench-mode-activate-existing"
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-xs font-semibold text-slate-900">Activate Existing Shell</p>
                    <p className="text-[10px] text-slate-500">Pre-onboarded employee record already exists. Just flip status to active + light updates.</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {showEmployeePicker && (
            <div>
              <label className="block text-xs font-medium mb-1">Employee *</label>
              <EmployeeSinglePicker
                companyId={companyId}
                value={employeeId}
                onChange={setEmployeeId}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Effective Date</label>
              <input data-testid="hr-bench-effective-date" type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
            </div>
          </div>

          {/* Per-category payload */}
          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-slate-700 mb-2">{catDef?.label} details</p>
            {renderPayloadFields(category, payload, setP, meta)}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Remarks</label>
            <textarea data-testid="hr-bench-remarks" value={remarks} rows={2} onChange={e => setRemarks(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="hr-bench-create-submit" onClick={submit} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Draft'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function PField({ label, fkey, type = 'text', placeholder, options, payload, setP }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      {options ? (
        <select
          value={payload[fkey] || ''}
          onChange={e => setP(fkey, e.target.value)}
          className="w-full px-3 py-2 border rounded text-sm"
          data-testid={`hr-bench-payload-${fkey}`}
        >
          <option value="">—</option>
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={payload[fkey] || ''}
          onChange={e => setP(fkey, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border rounded text-sm"
          data-testid={`hr-bench-payload-${fkey}`}
        />
      )}
    </div>
  );
}

function renderPayloadFields(category, payload, setP, meta) {
  const fieldsByCat = {
    new_joiner: (() => {
      const isCreateNew = (payload.creation_mode || 'create_new') === 'create_new';
      // Activate-existing path: keep the lightweight 6-field form.
      if (!isCreateNew) {
        return (
          <div className="grid grid-cols-2 gap-3">
            <PField label="DOJ *" fkey="date_of_joining" type="date" payload={payload} setP={setP} />
            <PField label="Department" fkey="department" payload={payload} setP={setP} />
            <PField label="Designation" fkey="designation" payload={payload} setP={setP} />
            <PField label="Location" fkey="location" payload={payload} setP={setP} />
            <PField label="Payroll Group" fkey="payroll_group" payload={payload} setP={setP} />
            <PField label="CTC" fkey="ctc_annual" type="number" placeholder="Annual ₹" payload={payload} setP={setP} />
          </div>
        );
      }
      // Stage 73.20z — create_new: full master field set, sectioned.
      return (
        <div className="space-y-4">
          {/* Personal */}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Personal</p>
            <div className="grid grid-cols-2 gap-3">
              <PField label="First Name *" fkey="first_name" payload={payload} setP={setP} />
              <PField label="Last Name" fkey="last_name" payload={payload} setP={setP} />
              <PField label="Date of Birth *" fkey="date_of_birth" type="date" payload={payload} setP={setP} />
              <PField label="Gender" fkey="gender" options={[
                {value:'male',label:'Male'},{value:'female',label:'Female'},{value:'other',label:'Other'}
              ]} payload={payload} setP={setP} />
              <PField label="Marital Status" fkey="marital_status" options={[
                {value:'single',label:'Single'},{value:'married',label:'Married'},{value:'divorced',label:'Divorced'},{value:'widowed',label:'Widowed'}
              ]} payload={payload} setP={setP} />
              <PField label="Mobile *" fkey="mobile" placeholder="10 digits" payload={payload} setP={setP} />
              <PField label="Personal Email" fkey="personal_email" type="email" payload={payload} setP={setP} />
              <PField label="Work Email" fkey="work_email" type="email" payload={payload} setP={setP} />
            </div>
          </div>
          {/* Org placement */}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Organisation</p>
            <div className="grid grid-cols-2 gap-3">
              <PField label="Date of Joining *" fkey="date_of_joining" type="date" payload={payload} setP={setP} />
              <PField label="Designation" fkey="designation" placeholder="name or code" payload={payload} setP={setP} />
              <PField label="Grade" fkey="grade" placeholder="E1 / E2 / M1 / M2" payload={payload} setP={setP} />
              <PField label="Department" fkey="department" payload={payload} setP={setP} />
              <PField label="Location" fkey="location" payload={payload} setP={setP} />
              <PField label="Employee Category" fkey="employee_category" placeholder="Regular / Contract" payload={payload} setP={setP} />
              <PField label="Employment Type" fkey="employment_type" placeholder="Permanent / Trainee" payload={payload} setP={setP} />
              <PField label="Payroll Group" fkey="payroll_group" placeholder="Default" payload={payload} setP={setP} />
              <PField label="Reporting Manager" fkey="reporting_manager" placeholder="Manager Name or Code" payload={payload} setP={setP} />
              <PField label="Cost Center" fkey="cost_center" placeholder="Cost Center Code" payload={payload} setP={setP} />
              <PField label="Business Unit" fkey="business_unit" placeholder="Business Unit" payload={payload} setP={setP} />
            </div>
          </div>
          {/* Address */}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Address</p>
            <div className="grid grid-cols-2 gap-3">
              <PField label="Current Address" fkey="current_address" payload={payload} setP={setP} />
              <PField label="Permanent Address" fkey="permanent_address" payload={payload} setP={setP} />
              <PField label="City" fkey="city" payload={payload} setP={setP} />
              <PField label="State" fkey="state" payload={payload} setP={setP} />
              <PField label="Country" fkey="country" payload={payload} setP={setP} />
              <PField label="Pincode" fkey="pincode" payload={payload} setP={setP} />
            </div>
          </div>
          {/* KYC / Statutory */}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">KYC &amp; Statutory IDs</p>
            <div className="grid grid-cols-2 gap-3">
              <PField label="PAN" fkey="pan_number" placeholder="ABCDE1234F" payload={payload} setP={setP} />
              <PField label="Aadhaar (12 digits)" fkey="aadhaar_number" payload={payload} setP={setP} />
              <PField label="UAN" fkey="uan_number" payload={payload} setP={setP} />
              <PField label="PF Number" fkey="pf_number" payload={payload} setP={setP} />
              <PField label="ESIC IIP" fkey="esic_iip_number" payload={payload} setP={setP} />
              <PField label="Professional Tax State" fkey="pt_state" placeholder="Tamil Nadu / Karnataka …" payload={payload} setP={setP} />
            </div>
          </div>
          {/* Bank */}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Bank</p>
            <div className="grid grid-cols-2 gap-3">
              <PField label="Bank Account" fkey="bank_account" payload={payload} setP={setP} />
              <PField label="IFSC" fkey="bank_ifsc" placeholder="HDFC0001234" payload={payload} setP={setP} />
              <PField label="Bank Name" fkey="bank_name" payload={payload} setP={setP} />
              <PField label="Branch" fkey="bank_branch" payload={payload} setP={setP} />
            </div>
          </div>
          {/* CTC */}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">CTC &amp; Salary</p>
            <div className="grid grid-cols-2 gap-3">
              <PField label="CTC Group Code" fkey="ctc_group_code" placeholder="STAFF_GENERAL / OM_FIELD_T1" payload={payload} setP={setP} />
              <PField label="CTC Annual (₹) *" fkey="ctc_annual" type="number" payload={payload} setP={setP} />
              <PField label="Skill Category (O&M only)" fkey="skill_category" placeholder="Highly Skilled / Skilled" payload={payload} setP={setP} />
              <PField label="Daily Wage Override (₹)" fkey="daily_wage_rate_override" type="number" placeholder="blank = master rate" payload={payload} setP={setP} />
            </div>
          </div>
        </div>
      );
    })(),
    resignation: (
      <div className="grid grid-cols-2 gap-3">
        <PField label="Resignation Date" fkey="resignation_date" type="date" payload={payload} setP={setP} />
        <PField label="Last Working Day" fkey="last_working_day" type="date" payload={payload} setP={setP} />
        <PField label="Notice Period (days)" fkey="notice_period_days" type="number" payload={payload} setP={setP} />
        <PField label="Payroll Stop Month (YYYY-MM)" fkey="payroll_stop_month" placeholder="2026-07" payload={payload} setP={setP} />
        <PField label="F&F Applicable" fkey="fnf_applicable" options={[{value:'true',label:'Yes'},{value:'false',label:'No'}]} payload={payload} setP={setP} />
        <PField label="Hold Salary" fkey="hold_salary" options={[{value:'true',label:'Yes'},{value:'false',label:'No'}]} payload={payload} setP={setP} />
        <div className="col-span-2"><PField label="Exit Remarks" fkey="exit_remarks" payload={payload} setP={setP} /></div>
      </div>
    ),
    salary_hold: (
      <PField label="Hold Reason *" fkey="hold_reason"
        options={meta.hold_reasons.map(r => ({ value: r, label: r.replace(/_/g, ' ') }))} payload={payload} setP={setP} />
    ),
    hold_release: <p className="text-xs text-slate-500">No additional fields — release will set status back to active.</p>,
    salary_revision: (
      <div className="grid grid-cols-2 gap-3">
        <PField label="New CTC *" fkey="new_ctc" type="number" payload={payload} setP={setP} />
        <PField label="Effective From *" fkey="effective_from" type="date" payload={payload} setP={setP} />
        <div className="col-span-2"><PField label="Reason" fkey="reason" payload={payload} setP={setP} /></div>
      </div>
    ),
    increment: (
      <div className="grid grid-cols-2 gap-3">
        <PField label="New CTC *" fkey="new_ctc" type="number" payload={payload} setP={setP} />
        <PField label="Effective From *" fkey="effective_from" type="date" payload={payload} setP={setP} />
        <div className="col-span-2"><PField label="Reason" fkey="reason" placeholder="Annual review / promotion" payload={payload} setP={setP} /></div>
      </div>
    ),
    designation_change: <PField label="New Designation *" fkey="new_designation" payload={payload} setP={setP} />,
    department_change:  <PField label="New Department *" fkey="new_department" payload={payload} setP={setP} />,
    location_change: (
      <div className="grid grid-cols-2 gap-3">
        <PField label="New Location" fkey="new_location" payload={payload} setP={setP} />
        <PField label="New Project" fkey="new_project" payload={payload} setP={setP} />
      </div>
    ),
    attendance_lop: (
      <div className="grid grid-cols-3 gap-3">
        <PField label="LOP Days *" fkey="days" type="number" payload={payload} setP={setP} />
        <PField label="Year *" fkey="statutory_year" type="number" payload={payload} setP={setP} />
        <PField label="Month *" fkey="statutory_month" type="number" payload={payload} setP={setP} />
      </div>
    ),
    other_monthly_input: (
      <div className="grid grid-cols-2 gap-3">
        <PField label="Head Code *" fkey="head_code" placeholder="CUSTOM_PAY" payload={payload} setP={setP} />
        <PField label="Head Type" fkey="head_type" options={[{value:'earning',label:'Earning'},{value:'deduction',label:'Deduction'}]} payload={payload} setP={setP} />
        <PField label="Amount *" fkey="amount" type="number" payload={payload} setP={setP} />
        <PField label="Year *" fkey="statutory_year" type="number" payload={payload} setP={setP} />
        <PField label="Month *" fkey="statutory_month" type="number" payload={payload} setP={setP} />
      </div>
    ),
  };

  // Default for all monthly_input categories (incentive, bonus, arrear, recovery, etc.)
  if (!fieldsByCat[category]) {
    return (
      <div className="grid grid-cols-3 gap-3">
        <PField label="Amount *" fkey="amount" type="number" payload={payload} setP={setP} />
        <PField label="Year *" fkey="statutory_year" type="number" payload={payload} setP={setP} />
        <PField label="Month *" fkey="statutory_month" type="number" payload={payload} setP={setP} />
      </div>
    );
  }
  return fieldsByCat[category];
}

function BulkUploadModal({ meta, companyId, onClose, onUploaded }) {
  // Two tabs: Master Workbook (primary — single multi-sheet XLSX) and
  // Per-Category (fallback for one-off CSV uploads, kept for backward compat).
  const [tab, setTab] = useState('master');
  const [activeCategory, setActiveCategory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [masterResult, setMasterResult] = useState(null);
  const fileInputRefs = React.useRef({});
  const masterFileRef = React.useRef(null);

  const grouped = useMemo(() => {
    const out = {};
    meta.categories.forEach(c => {
      if (!out[c.group]) out[c.group] = [];
      out[c.group].push(c);
    });
    return out;
  }, [meta.categories]);

  const downloadMasterTemplate = async () => {
    try {
      const r = await axios.get(`${API}/hr-bench/master-template.xlsx`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'hr_bench_master_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Master template downloaded — one sheet per category');
    } catch (e) { toast.error('Master template download failed'); }
  };

  const uploadMaster = async (file) => {
    if (!file) return;
    if (!companyId) { toast.error('Select a company first'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', companyId);
      const r = await axios.post(`${API}/hr-bench/master-upload`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setMasterResult(r.data);
      const { total_created = 0, total_failed = 0 } = r.data;
      if (total_failed > 0) {
        toast.warning(`Master upload: ${total_created} created, ${total_failed} failed`);
      } else {
        toast.success(`Master upload: ${total_created} draft entries across ${r.data.sheets_processed} sheets`);
      }
      onUploaded();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const downloadTemplate = async (catKey) => {
    try {
      const r = await axios.get(
        `${API}/hr-bench/bulk-upload-template?category=${catKey}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `hr_bench_${catKey}_template.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Template download failed'); }
  };

  const uploadForCategory = async (catKey, file) => {
    if (!file) return;
    if (!companyId) { toast.error('Select a company first'); return; }
    setBusy(true);
    setActiveCategory(catKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', catKey);
      fd.append('company_id', companyId);
      const r = await axios.post(`${API}/hr-bench/bulk-upload`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const { rows_created = 0, rows_failed = 0, errors = [] } = r.data;
      setLastResult({ catKey, rows_created, rows_failed, errors });
      if (rows_failed > 0) {
        toast.warning(`${catKey}: created ${rows_created}, ${rows_failed} failed`);
      } else {
        toast.success(`${meta.categories.find(c => c.key === catKey)?.label}: ${rows_created} draft entries created`);
      }
      onUploaded();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="hr-bench-bulk-modal">
      <Card className="max-w-4xl w-full p-6 bg-white my-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold">Bulk Upload</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="hr-bench-bulk-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-slate-200">
          <button
            data-testid="hr-bench-bulk-tab-master"
            onClick={() => setTab('master')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'master' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <span className="inline-flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Master Workbook (recommended)</span>
          </button>
          <button
            data-testid="hr-bench-bulk-tab-per-category"
            onClick={() => setTab('per-category')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'per-category' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <span className="inline-flex items-center gap-2"><Upload className="w-4 h-4" />Per-Category CSV</span>
          </button>
        </div>

        {tab === 'master' ? (
          <div className="space-y-4" data-testid="hr-bench-bulk-master-tab">
            <Card className="p-4 bg-violet-50 border-violet-200">
              <h3 className="text-sm font-bold text-violet-900 mb-2">One workbook, all 22 categories</h3>
              <ol className="text-xs text-violet-800 space-y-1 list-decimal list-inside">
                <li>Download the master template (one Excel file, 22 sheets — one per category).</li>
                <li>Open in Excel / Google Sheets. <strong>Employee Code is column A on every sheet.</strong></li>
                <li>Fill rows only on the sheets you need; leave the others empty (they'll be skipped silently).</li>
                <li>Save and upload back here — entries are created as drafts, ready for HR approval.</li>
              </ol>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="p-4">
                <h4 className="text-sm font-semibold mb-2">1. Download Template</h4>
                <p className="text-xs text-slate-600 mb-3">Multi-sheet XLSX. Contains a README and one tab per HR Bench category with the expected columns + a sample row you can delete or overwrite.</p>
                <Button onClick={downloadMasterTemplate} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="hr-bench-master-template-btn">
                  <Download className="w-4 h-4 mr-1" /> Download Master Template (.xlsx)
                </Button>
              </Card>
              <Card className="p-4">
                <h4 className="text-sm font-semibold mb-2">2. Upload Filled Workbook</h4>
                <p className="text-xs text-slate-600 mb-3">Same workbook back — system reads every non-empty sheet and creates one draft per row. Sample rows (with EMP-2026-0001) are auto-ignored.</p>
                <input
                  ref={masterFileRef}
                  type="file" accept=".xlsx" className="hidden"
                  onChange={(e) => { uploadMaster(e.target.files[0]); e.target.value = ''; }}
                  data-testid="hr-bench-master-upload-input"
                />
                <Button
                  onClick={() => masterFileRef.current?.click()}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="hr-bench-master-upload-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  Upload Master Workbook (.xlsx)
                </Button>
              </Card>
            </div>

            {masterResult && (
              <Card className="p-4 bg-slate-50 border-slate-300" data-testid="hr-bench-master-result">
                <h4 className="text-sm font-semibold mb-2 flex items-center justify-between">
                  <span>Upload result — {masterResult.total_created} created, {masterResult.total_failed} failed</span>
                  <button onClick={() => setMasterResult(null)} className="text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>
                </h4>
                <div className="max-h-60 overflow-y-auto text-xs">
                  <table className="w-full">
                    <thead className="text-slate-500">
                      <tr><th className="text-left px-2 py-1">Sheet</th><th className="text-left px-2 py-1">Category</th><th className="text-right px-2 py-1">Created</th><th className="text-right px-2 py-1">Failed</th></tr>
                    </thead>
                    <tbody>
                      {masterResult.results
                        .filter(s => s.rows_created > 0 || s.rows_failed > 0)
                        .map((s, i) => (
                          <tr key={i} className="border-t border-slate-200">
                            <td className="px-2 py-1">{s.sheet}</td>
                            <td className="px-2 py-1 text-slate-600">{s.category_label || s.category}</td>
                            <td className="px-2 py-1 text-right text-emerald-700">{s.rows_created}</td>
                            <td className={`px-2 py-1 text-right ${s.rows_failed > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{s.rows_failed}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {masterResult.results.filter(s => s.errors?.length).map((s, i) => (
                    <details key={i} className="mt-2">
                      <summary className="cursor-pointer text-rose-700">{s.sheet} errors ({s.errors.length})</summary>
                      <ul className="ml-4 mt-1 list-disc text-rose-600">
                        {s.errors.slice(0, 10).map((e, j) => <li key={j}>Row {e.row}: {e.error}</li>)}
                      </ul>
                    </details>
                  ))}
                  {masterResult.sheets_skipped?.length > 0 && (
                    <div className="mt-2 text-slate-500 italic">
                      Skipped unrecognized sheets: {masterResult.sheets_skipped.join(', ')}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        ) : (
          <div data-testid="hr-bench-bulk-per-category-tab">
            <p className="text-xs text-slate-600 mb-4">
              Fallback: download / upload a single CSV per category. Useful for one-off
              corrections or when you only need a single category and don't want the full workbook.
            </p>
            {lastResult && (
              <div className={`mb-4 p-3 rounded border ${lastResult.rows_failed > 0 ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}
                  data-testid="hr-bench-bulk-result">
                <div className="text-sm font-semibold">
                  Last upload: {meta.categories.find(c => c.key === lastResult.catKey)?.label} —{' '}
                  <span className="text-emerald-700">{lastResult.rows_created} created</span>
                  {lastResult.rows_failed > 0 && <> • <span className="text-amber-700">{lastResult.rows_failed} failed</span></>}
                </div>
              </div>
            )}
            <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-2">
              {Object.entries(grouped).map(([group, cats]) => (
                <div key={group}>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 sticky top-0 bg-white py-1">
                    {group} ({cats.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {cats.map(c => (
                      <div key={c.key}
                          className="border border-slate-200 rounded-lg p-3 hover:border-violet-400 transition flex items-center justify-between gap-2"
                          data-testid={`hr-bench-bulk-cat-${c.key}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{c.label}</div>
                          <div className="text-[10px] text-slate-500 uppercase">{c.kind.replace('_', ' ')}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost"
                            onClick={() => downloadTemplate(c.key)}
                            title={`Download CSV template for ${c.label}`}
                            data-testid={`hr-bench-bulk-template-${c.key}`}>
                            <Download className="w-3.5 h-3.5 text-violet-600" />
                          </Button>
                          <input ref={el => (fileInputRefs.current[c.key] = el)}
                            type="file" accept=".csv" className="hidden"
                            onChange={e => { uploadForCategory(c.key, e.target.files[0]); e.target.value = ''; }}
                            data-testid={`hr-bench-bulk-input-${c.key}`} />
                          <Button size="sm"
                            onClick={() => fileInputRefs.current[c.key]?.click()}
                            disabled={busy && activeCategory === c.key}
                            className="text-xs bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid={`hr-bench-bulk-upload-${c.key}`}>
                            {busy && activeCategory === c.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" />Upload</>}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  );
}

function EntryDetailDrawer({ entryId, category, onClose, onDelete, onChanged }) {
  const [entry, setEntry] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [busy, setBusy] = useState(false);


  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hr-bench/entries/${entryId}`, { withCredentials: true });
      setEntry(r.data);
    } catch (e) { toast.error('Load failed'); }
  }, [entryId]);

  useEffect(() => { load(); }, [load]);

  if (!entry) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
        <div className="bg-white w-[600px] h-full p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </div>
    );
  }

  const tone = STATE_TONE[entry.status] || STATE_TONE.draft;
  const next = STATE_NEXT_LABEL[entry.status];
  const canReject = ['draft','submitted','hr_approved','payroll_review'].includes(entry.status);
  const supportsLetter = ['increment_letter','appointment_letter','relieving_letter',
                          'salary_revision','increment','resignation','new_joiner'].includes(entry.category);

  const downloadLetter = async () => {
    try {
      const r = await axios.get(`${API}/hr-bench/entries/${entry.id}/letter.pdf`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `hr_bench_${entry.category}_${entry._employee?.code || entry.id.slice(0,8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Letter downloaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'PDF generation failed'); }
  };

  const advance = async () => {
    if (!next) return;
    setBusy(true);
    try {
      await axios.post(`${API}/hr-bench/entries/${entry.id}${TRANSITION_PATH[next.to]}`, {}, { withCredentials: true });
      toast.success(`Moved to ${STATE_TONE[next.to].label}`);
      await load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const reject = async () => {
    if (!rejectRemarks.trim()) { toast.error('Rejection remarks required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/hr-bench/entries/${entry.id}/reject`, { remarks: rejectRemarks }, { withCredentials: true });
      toast.success('Entry rejected');
      setRejectOpen(false); setRejectRemarks(''); await load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const uploadDoc = async (f) => {
    if (!f) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('doc_type', 'supporting');
      await axios.post(`${API}/hr-bench/entries/${entry.id}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document attached');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setUploadBusy(false);
  };

  const NextIcon = next?.icon;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="hr-bench-detail-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{category?.label || entry.category}</h2>
            <span className={`text-[11px] font-medium px-2 py-1 rounded border ${tone.bg} ${tone.txt} ${tone.bd} mt-1 inline-block`}>{tone.label}</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          {entry._employee && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
              <div className="font-semibold text-slate-800">{entry._employee.name}</div>
              <div className="text-slate-600">{entry._employee.code} • {entry._employee.designation} • {entry._employee.department}</div>
              {entry._employee.ctc !== undefined && <div className="text-slate-500 mt-1">Current CTC: ₹{Number(entry._employee.ctc || 0).toLocaleString('en-IN')}</div>}
            </div>
          )}

          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Payload</h3>
            <div className="bg-slate-50 rounded p-3 text-xs">
              {Object.keys(entry.payload || {}).length === 0 ? (
                <span className="text-slate-400">No payload</span>
              ) : (
                <table className="w-full">
                  <tbody>
                    {Object.entries(entry.payload).map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-200 last:border-0">
                        <td className="py-1 pr-3 text-slate-600 capitalize">{k.replace(/_/g, ' ')}</td>
                        <td className="py-1 font-medium text-slate-800">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {entry.applied_summary && (
            <div>
              <h3 className="text-xs font-semibold text-emerald-700 uppercase mb-2">Applied summary (on payroll-accept)</h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
                <pre className="text-emerald-900 whitespace-pre-wrap">{JSON.stringify(entry.applied_summary, null, 2)}</pre>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center justify-between">
              <span>Documents</span>
              <label className="text-violet-700 hover:underline cursor-pointer text-[11px]">
                <input type="file" className="hidden" onChange={e => uploadDoc(e.target.files[0])} data-testid="hr-bench-upload-input" />
                {uploadBusy ? 'Uploading…' : '+ Attach'}
              </label>
            </h3>
            {(entry.documents || []).length === 0 ? (
              <p className="text-xs text-slate-400">No documents attached</p>
            ) : (
              <ul className="space-y-1">
                {entry.documents.map((d, i) => (
                  <li key={i} className="text-xs flex items-center gap-2 text-slate-700">
                    <FileText className="w-3 h-3" /> {d.filename}
                    <span className="text-slate-400">({Math.round(d.size / 1024)} KB)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2"><History className="w-3 h-3" />Audit trail</h3>
            <div className="space-y-1">
              {(entry.history || []).map((h, i) => (
                <div key={i} className="text-xs border-l-2 border-violet-200 pl-3 py-1">
                  <div className="font-medium text-slate-800">
                    {h.from ? `${STATE_TONE[h.from]?.label || h.from} → ` : ''}
                    {STATE_TONE[h.to]?.label || h.to}
                  </div>
                  <div className="text-slate-500">{h.by_name} • {new Date(h.at).toLocaleString()}</div>
                  {h.remarks && <div className="text-slate-600 italic mt-0.5">"{h.remarks}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={onDelete}
            className="text-rose-600 border-rose-300 hover:bg-rose-50"
            data-testid="hr-bench-delete-detail"
          >
            <Trash2 className="w-4 h-4 mr-1 text-rose-600" /> Delete
          </Button>
          {supportsLetter && (
            <Button variant="outline" onClick={downloadLetter} data-testid="hr-bench-letter-pdf-btn">
              <FileText className="w-4 h-4 mr-1 text-violet-700" /> Download Letter PDF
            </Button>
          )}
          {canReject && (
            <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="hr-bench-reject-btn">
              <XCircle className="w-4 h-4 mr-1 text-rose-600" /> Reject
            </Button>
          )}
          {next && (
            <Button onClick={advance} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="hr-bench-advance-detail">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <NextIcon className="w-4 h-4 mr-1" />}
              {next.label}
            </Button>
          )}
        </div>
      </div>

      {rejectOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-5 bg-white">
            <h3 className="font-bold mb-3">Reject Entry</h3>
            <textarea
              data-testid="hr-bench-reject-remarks"
              value={rejectRemarks}
              rows={3}
              onChange={e => setRejectRemarks(e.target.value)}
              placeholder="Reason for rejection (required)..."
              className="w-full px-3 py-2 border rounded text-sm" />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="hr-bench-reject-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function EmployeeSinglePicker({ companyId, value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Pre-fetch the currently-selected employee label
  useEffect(() => {
    if (!value) { setSelectedLabel(''); return; }
    axios.get(`${API}/employees/${value}`, { withCredentials: true })
      .then(r => {
        const ed = r.data?.employment_details || {};
        const pd = r.data?.personal_details || {};
        const label = `${ed.employee_code || ''} — ${(pd.first_name || '') + ' ' + (pd.last_name || '')}`.trim();
        setSelectedLabel(label);
      }).catch(() => setSelectedLabel(value.slice(0, 8)));
  }, [value]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ search: query.trim(), limit: '12' });
        if (companyId) qs.set('company_id', companyId);
        const r = await axios.get(`${API}/employees?${qs.toString()}`, { withCredentials: true });
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setResults(items.slice(0, 12).map(e => ({
          id: e.id,
          code: e.employment_details?.employee_code || '',
          name: `${e.personal_details?.first_name || ''} ${e.personal_details?.last_name || ''}`.trim(),
          designation: e.employment_details?.designation || '',
        })));
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [query, companyId]);

  return (
    <div className="relative" data-testid="hr-bench-employee-picker">
      <div className="flex items-center border border-slate-300 rounded px-2 py-1.5">
        <Search className="w-3 h-3 text-slate-400 mr-1.5 shrink-0" />
        {value && !query ? (
          <div className="flex-1 flex items-center gap-2">
            <span className="text-xs bg-violet-100 text-violet-800 px-2 py-0.5 rounded">{selectedLabel || value.slice(0, 8)}</span>
            <button onClick={() => { onChange(''); setSelectedLabel(''); }} className="text-slate-400 hover:text-rose-600" title="Change">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <input
            data-testid="hr-bench-employee-search"
            value={query}
            onChange={e => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            placeholder="Search by code or name..."
            className="flex-1 text-sm outline-none border-0 bg-transparent" />
        )}
      </div>
      {showResults && results.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-56 overflow-y-auto">
          {results.map(e => (
            <button
              key={e.id}
              onMouseDown={() => { onChange(e.id); setSelectedLabel(`${e.code} — ${e.name}`); setQuery(''); setResults([]); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 flex items-center justify-between"
              data-testid={`hr-bench-emp-option-${e.code}`}>
              <span>
                <span className="font-mono text-violet-700">{e.code}</span>
                <span className="text-slate-500 ml-2">{e.name}</span>
              </span>
              <span className="text-slate-400 text-[10px]">{e.designation}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeleteConfirmModal({ entry, category, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const payloadPreview = (() => {
    const p = entry.payload || {};
    const parts = [];
    if (p.amount) parts.push(`₹${Number(p.amount).toLocaleString('en-IN')}`);
    if (p.new_ctc) parts.push(`CTC → ₹${Number(p.new_ctc).toLocaleString('en-IN')}`);
    if (p.new_designation) parts.push(`Designation → ${p.new_designation}`);
    if (p.new_department) parts.push(`Department → ${p.new_department}`);
    if (p.new_location) parts.push(`Location → ${p.new_location}`);
    if (p.last_working_day || p.last_working_date) parts.push(`LWD ${p.last_working_day || p.last_working_date}`);
    if (p.hold_reason) parts.push(`Hold Reason: ${p.hold_reason.replace(/_/g, ' ')}`);
    if (p.statutory_year && p.statutory_month) parts.push(`Period: ${p.statutory_month}/${p.statutory_year}`);
    return parts.join(' • ') || (entry.remarks || '—');
  })();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/hr-bench/entries/${entry.id}`, { withCredentials: true });
      toast.success('HR Bench entry deleted successfully');
      onDeleted();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDeleting(false);
  };

  const empName = entry._employee?.name || entry.employee_name || entry.employee_id || 'Unknown Employee';
  const empCode = entry._employee?.code || entry.employee_code || '';

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" data-testid="hr-bench-delete-modal">
      <Card className="max-w-md w-full p-6 bg-white shadow-2xl rounded-xl space-y-4">
        <div className="flex items-center gap-3 text-rose-600">
          <div className="p-2.5 bg-rose-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Delete HR Bench Entry?</h2>
            <p className="text-xs text-slate-500">This action cannot be undone.</p>
          </div>
        </div>

        <div className="text-xs text-slate-600 bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-2">
          <p className="font-medium text-slate-800 text-xs mb-1">
            This will permanently remove this HR Bench entry and its related records generated from this entry.
          </p>
          <div className="grid grid-cols-3 gap-1 pt-1 text-slate-700">
            <span className="font-semibold text-slate-500">Employee:</span>
            <span className="col-span-2 font-medium">{empName} {empCode ? `(${empCode})` : ''}</span>

            <span className="font-semibold text-slate-500">Category:</span>
            <span className="col-span-2 font-medium">{category?.label || entry.category}</span>

            <span className="font-semibold text-slate-500">Effective Date:</span>
            <span className="col-span-2">{entry.effective_date || 'N/A'}</span>

            <span className="font-semibold text-slate-500">Payload:</span>
            <span className="col-span-2 truncate" title={payloadPreview}>{payloadPreview}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={deleting} data-testid="hr-bench-delete-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="bg-rose-600 hover:bg-rose-700 text-white font-medium px-4"
            data-testid="hr-bench-delete-confirm"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Delete
          </Button>
        </div>
      </Card>
    </div>
  );
}

